import { useEffect, useState } from 'react';
import { countdownService } from '../services/countdownService';

/**
 * Loads the single org-wide countdown once per mount.
 *
 * Kept apart from `useCountdown`, which is pure clock math on an already-known
 * target: the display ticks once a second and must never refetch on that
 * cadence. This runs exactly once and hands the ticking hook a stable ISO
 * string.
 *
 * Failures resolve to `null` rather than throwing. The consumer is the app
 * header, and a countdown that could not be fetched must not take the header
 * down with it — an absent countdown and an unreachable one read the same to
 * the viewer, which is the right outcome for decoration.
 *
 * @returns {{countdown: object|null, loading: boolean}}
 */
export function useCountdownQuery() {
    const [countdown, setCountdown] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await countdownService.getCountdown();
                if (!cancelled) setCountdown(data);
            } catch {
                if (!cancelled) setCountdown(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    return { countdown, loading };
}

export default useCountdownQuery;
